import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchRecordingSummaryById, fetchRecordingTranscriptById, generateRecordingSummaryById, getAiNotesServiceUrl, MeetingAiApiError, MS_PER_SECOND, REC_SUMMARY_POLL_INTERVAL_MS, REC_SUMMARY_POLL_MAX_ATTEMPTS, REC_SUMMARY_STATUS_FAILED, REC_SUMMARY_STATUS_PENDING, REC_SUMMARY_STATUS_PROCESSING, REC_SUMMARY_STATUS_READY, regenerateRecordingSummaryById } from '../../ai-notes/functions';
import { PortalApiError, portalApi } from '../api';
import { getAccessToken, getStoredUser } from '../authStorage';
import { RECORDINGS_RECENT_IDS_MAX, STORAGE_RECORDING_RECENT_IDS } from '../constants';
import { DEFAULT_RECORDING_LIST_FILTER, formatMeetingNo, formatRecordingDuration, formatRecordingListTime, isRecordingListFilterActive, matchesRecordingListFilter } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { useIsPortalMobile } from '../useIsPortalMobile';
import RecordingFilterPanel from './RecordingFilterPanel';
/**
 * 读取最近浏览录制 ID。
 *
 * @returns {number[]}
 */
function loadRecentRecordingIds() {
    try {
        const raw = window.localStorage.getItem(STORAGE_RECORDING_RECENT_IDS);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0)
            .slice(0, RECORDINGS_RECENT_IDS_MAX);
    }
    catch (e) {
        logger.warn(`读取最近浏览失败 | ${e?.message || e}`);
        return [];
    }
}
/**
 * 写入最近浏览录制 ID。
 *
 * @param {number[]} ids - ID 列表。
 * @returns {void}
 */
function saveRecentRecordingIds(ids) {
    try {
        window.localStorage.setItem(STORAGE_RECORDING_RECENT_IDS, JSON.stringify(ids.slice(0, RECORDINGS_RECENT_IDS_MAX)));
    }
    catch (e) {
        logger.warn(`保存最近浏览失败 | ${e?.message || e}`);
    }
}
/**
 * 会议录制页：列表态全宽列表；详情态左播放+总结、右会议列表可切换。
 * 会议总结需手动点击触发，播放仅只读同步已有状态。
 */
export default function RecordingsPage({ initialRecordingId }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [items, setItems] = useState([]);
    const [playing, setPlaying] = useState(null);
    const [playUrl, setPlayUrl] = useState('');
    const [playError, setPlayError] = useState('');
    const [summary, setSummary] = useState(null);
    const [transcript, setTranscript] = useState([]);
    const [summaryBusy, setSummaryBusy] = useState(false);
    const [summaryGenerating, setSummaryGenerating] = useState(false);
    const [summaryError, setSummaryError] = useState('');
    const [summaryRecordingId, setSummaryRecordingId] = useState(null);
    const [summaryTitle, setSummaryTitle] = useState('');
    /** 未总结时仅显示「会议总结」按钮，不展示总结内容区 */
    const [showSummaryCta, setShowSummaryCta] = useState(false);
    const [tab, setTab] = useState('all');
    const [viewMode, setViewMode] = useState('grid');
    const [listFilter, setListFilter] = useState(DEFAULT_RECORDING_LIST_FILTER);
    const [draftFilter, setDraftFilter] = useState(DEFAULT_RECORDING_LIST_FILTER);
    const [filterOpen, setFilterOpen] = useState(false);
    const isPortalMobile = useIsPortalMobile();
    const [menuRecordingId, setMenuRecordingId] = useState(null);
    const [recentIds, setRecentIds] = useState(() => loadRecentRecordingIds());
    const [toast, setToast] = useState('');
    const serviceUrl = useSelector((state) => getAiNotesServiceUrl(state));
    const filterBtnRef = useRef(null);
    const videoRef = useRef(null);
    const pollCancelledRef = useRef(false);
    /** 当前总结目标 recordingId，供异步回调做 stale guard（避免 hooks 闭包过期）。 */
    const summaryRecordingIdRef = useRef(null);
    /** 只读状态加载序号，避免快切播放时串台。 */
    const summaryStatusLoadSeqRef = useRef(0);
    /** 已自动打开过的历史跳转 recordingId，避免列表刷新重复播放。 */
    const openedInitialRecordingRef = useRef(null);
    const token = () => getAccessToken() || '';
    /**
     * 同步 summaryRecordingId 状态与 ref。
     *
     * @param {number|null} recordingId - 录制 ID。
     * @returns {void}
     */
    const setSummaryRecordingTarget = (recordingId) => {
        summaryRecordingIdRef.current = recordingId;
        setSummaryRecordingId(recordingId);
    };
    const load = useCallback(async () => {
        setBusy(true);
        setError('');
        try {
            const list = await portalApi.listRecordings(token());
            setItems(list);
            logger.info(`加载录制列表 | count=${list.length}`);
        }
        catch (e) {
            const msg = e instanceof PortalApiError ? e.message : M.LOAD_FAILED;
            setError(msg);
            logger.error(`加载录制列表失败 | ${e?.message}`);
        }
        finally {
            setBusy(false);
        }
    }, []);
    useEffect(() => {
        load();
        return () => {
            // 仅停止前端轮询；后端生成任务继续执行。
            pollCancelledRef.current = true;
        };
    }, [load]);
    useEffect(() => {
        const onDocClick = () => {
            setMenuRecordingId(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);
    const formatSize = (bytes) => {
        if (bytes == null || bytes < 0) {
            return '-';
        }
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };
    /**
     * 重置会议总结 UI（不取消后端已排队的生成任务）。
     *
     * @returns {void}
     */
    const resetSummaryUi = () => {
        pollCancelledRef.current = true;
        summaryStatusLoadSeqRef.current += 1;
        setSummary(null);
        setTranscript([]);
        setSummaryBusy(false);
        setSummaryGenerating(false);
        setSummaryError('');
        setSummaryRecordingTarget(null);
        setSummaryTitle('');
        setShowSummaryCta(false);
    };
    const closePlayer = () => {
        setPlaying(null);
        setPlayUrl('');
        setPlayError('');
        resetSummaryUi();
    };
    const onVideoError = () => {
        setPlayError(M.RECORDINGS_PLAY_FAILED);
        logger.error(`视频加载失败 | recordingId=${playing?.id ?? '-'} url=${playUrl}`);
    };
    const pollUntilDone = async (recordingId) => {
        for (let i = 0; i < REC_SUMMARY_POLL_MAX_ATTEMPTS; i++) {
            if (pollCancelledRef.current || summaryRecordingIdRef.current !== recordingId) {
                throw new Error('cancelled');
            }
            await new Promise(r => setTimeout(r, REC_SUMMARY_POLL_INTERVAL_MS));
            if (pollCancelledRef.current || summaryRecordingIdRef.current !== recordingId) {
                throw new Error('cancelled');
            }
            const next = await fetchRecordingSummaryById(serviceUrl, recordingId);
            if (next.status === REC_SUMMARY_STATUS_READY
                || next.status === REC_SUMMARY_STATUS_FAILED) {
                return next;
            }
        }
        throw new Error(M.RECORDINGS_SUMMARY_LOAD_FAILED);
    };
    const applyReady = async (recordingId, next) => {
        if (summaryRecordingIdRef.current !== recordingId) {
            logger.info(`丢弃过期会议总结结果 | recordingId=${recordingId} current=${summaryRecordingIdRef.current}`);
            return;
        }
        let lines = [];
        if (next.status === REC_SUMMARY_STATUS_READY) {
            try {
                lines = await fetchRecordingTranscriptById(serviceUrl, recordingId);
            }
            catch (e) {
                logger.warn(`逐字稿加载失败 | recordingId=${recordingId} ${e?.message || e}`);
            }
        }
        if (summaryRecordingIdRef.current !== recordingId) {
            logger.info(`丢弃过期会议总结结果（逐字稿后） | recordingId=${recordingId} current=${summaryRecordingIdRef.current}`);
            return;
        }
        setShowSummaryCta(false);
        setSummary(next.status === REC_SUMMARY_STATUS_READY ? next : null);
        setTranscript(lines);
        setSummaryBusy(false);
        setSummaryGenerating(false);
        setSummaryError(next.status === REC_SUMMARY_STATUS_FAILED
            ? (next.errorMessage || M.RECORDINGS_SUMMARY_FAILED)
            : '');
    };
    /**
     * 处理总结 API 错误并写入 UI。
     *
     * @param {number} recordingId - 录制 ID。
     * @param {any} e - 异常。
     * @returns {void}
     */
    const handleSummaryError = (recordingId, e) => {
        if (e?.message === 'cancelled') {
            return;
        }
        if (summaryRecordingIdRef.current !== recordingId) {
            return;
        }
        let message = M.RECORDINGS_SUMMARY_LOAD_FAILED;
        if (e instanceof MeetingAiApiError) {
            message = e.message || message;
        }
        else if (e?.message) {
            message = String(e.message);
        }
        setShowSummaryCta(false);
        setSummary(null);
        setTranscript([]);
        setSummaryBusy(false);
        setSummaryGenerating(false);
        setSummaryError(message);
        logger.error(`会议总结失败 | recordingId=${recordingId} ${e?.message || e}`);
    };
    /**
     * 播放后只读加载总结状态：已有则展示，pending 则显示 CTA，不自动 generate。
     *
     * @param {IRecordingItem} item - 录制项。
     * @returns {Promise<void>}
     */
    const loadSummaryStatus = async (item) => {
        const recordingId = Number(item.id);
        const title = item.title || item.fileName || String(recordingId || '');
        if (!recordingId || item.fileAvailable === false) {
            setShowSummaryCta(false);
            setSummary(null);
            setTranscript([]);
            setSummaryBusy(false);
            setSummaryGenerating(false);
            setSummaryError(M.RECORDINGS_SUMMARY_NEED_FILE);
            setSummaryRecordingTarget(recordingId || null);
            setSummaryTitle(title);
            logger.warn(`无法加载会议总结：文件不可用 | recordingId=${item.id}`);
            return;
        }
        // 只读探测阶段不展示总结区，避免未总结时闪出「正在生成」。
        pollCancelledRef.current = true;
        pollCancelledRef.current = false;
        const loadSeq = ++summaryStatusLoadSeqRef.current;
        setShowSummaryCta(false);
        setSummary(null);
        setTranscript([]);
        setSummaryBusy(false);
        setSummaryGenerating(false);
        setSummaryError('');
        setSummaryRecordingTarget(null);
        setSummaryTitle(title);
        logger.info(`只读加载会议总结状态 | recordingId=${recordingId}`);
        try {
            const next = await fetchRecordingSummaryById(serviceUrl, recordingId);
            if (loadSeq !== summaryStatusLoadSeqRef.current) {
                return;
            }
            setSummaryRecordingTarget(recordingId);
            if (next.status === REC_SUMMARY_STATUS_READY
                || next.status === REC_SUMMARY_STATUS_FAILED) {
                await applyReady(recordingId, next);
                logger.info(`会议总结状态已同步 | recordingId=${recordingId} status=${next.status}`);
                return;
            }
            if (next.status === REC_SUMMARY_STATUS_PROCESSING) {
                setShowSummaryCta(false);
                setSummaryBusy(true);
                setSummaryGenerating(true);
                setSummaryError('');
                const done = await pollUntilDone(recordingId);
                if (loadSeq !== summaryStatusLoadSeqRef.current
                    || summaryRecordingIdRef.current !== recordingId) {
                    return;
                }
                await applyReady(recordingId, done);
                logger.info(`会议总结轮询完成 | recordingId=${recordingId} status=${done.status}`);
                return;
            }
            // pending / 无有效总结：仅显示「会议总结」按钮
            setShowSummaryCta(true);
            setSummary(null);
            setTranscript([]);
            setSummaryBusy(false);
            setSummaryGenerating(false);
            setSummaryError('');
            logger.info(`会议总结未生成，显示 CTA | recordingId=${recordingId}`);
        }
        catch (e) {
            if (e?.message === 'cancelled' || loadSeq !== summaryStatusLoadSeqRef.current) {
                return;
            }
            // 查询失败时仍给出手动触发入口，避免卡死
            setSummaryRecordingTarget(recordingId);
            setShowSummaryCta(true);
            setSummary(null);
            setTranscript([]);
            setSummaryBusy(false);
            setSummaryGenerating(false);
            setSummaryError('');
            logger.warn(`加载会议总结状态失败，回退 CTA | recordingId=${recordingId} ${e?.message || e}`);
        }
    };
    /**
     * 手动点击「会议总结」：未总结才 generate；已 ready 只展示；failed（含无音频）不自动重试。
     *
     * @param {IRecordingItem} item - 录制项。
     * @returns {Promise<void>}
     */
    const onStartSummary = async (item) => {
        const recordingId = Number(item.id);
        if (!recordingId || item.fileAvailable === false) {
            setShowSummaryCta(false);
            setSummary(null);
            setTranscript([]);
            setSummaryBusy(false);
            setSummaryGenerating(false);
            setSummaryError(M.RECORDINGS_SUMMARY_NEED_FILE);
            setSummaryRecordingTarget(recordingId || null);
            setSummaryTitle(item.title || item.fileName || '');
            logger.warn(`无法生成会议总结：文件不可用 | recordingId=${item.id}`);
            return;
        }
        pollCancelledRef.current = true;
        pollCancelledRef.current = false;
        summaryStatusLoadSeqRef.current += 1;
        setShowSummaryCta(false);
        setSummaryBusy(true);
        setSummaryGenerating(false);
        setSummaryError('');
        setSummary(null);
        setTranscript([]);
        setSummaryRecordingTarget(recordingId);
        setSummaryTitle(item.title || item.fileName || String(recordingId));
        logger.info(`手动开始会议总结 | recordingId=${recordingId} serviceUrl=${serviceUrl}`);
        const started = Date.now();
        try {
            let next = await fetchRecordingSummaryById(serviceUrl, recordingId);
            if (summaryRecordingIdRef.current !== recordingId) {
                return;
            }
            if (next.status === REC_SUMMARY_STATUS_READY
                || next.status === REC_SUMMARY_STATUS_FAILED) {
                await applyReady(recordingId, next);
                logger.info(`会议总结已存在直接展示 | recordingId=${recordingId} status=${next.status}`);
                return;
            }
            if (next.status === REC_SUMMARY_STATUS_PENDING) {
                setSummaryGenerating(true);
                next = await generateRecordingSummaryById(serviceUrl, recordingId);
            }
            if (summaryRecordingIdRef.current !== recordingId) {
                return;
            }
            if (next.status === REC_SUMMARY_STATUS_READY
                || next.status === REC_SUMMARY_STATUS_FAILED) {
                await applyReady(recordingId, next);
                return;
            }
            if (next.status === REC_SUMMARY_STATUS_PROCESSING) {
                setSummaryGenerating(true);
                next = await pollUntilDone(recordingId);
            }
            if (summaryRecordingIdRef.current !== recordingId) {
                return;
            }
            await applyReady(recordingId, next);
            logger.info(`会议总结完成 | recordingId=${recordingId} status=${next.status} elapsedMs=${Date.now() - started}`);
        }
        catch (e) {
            handleSummaryError(recordingId, e);
        }
    };
    /**
     * 播放录制；只读同步总结状态，不自动触发生成。
     *
     * @param {IRecordingItem} item - 录制项。
     * @returns {void}
     */
    const onPlay = (item) => {
        if (item.fileAvailable === false) {
            setPlaying(null);
            setPlayUrl('');
            resetSummaryUi();
            setPlayError(M.RECORDINGS_FILE_MISSING);
            logger.warn(`录制文件不可用 | recordingId=${item.id} sessionId=${item.sessionId}`);
            return;
        }
        const url = portalApi.getRecordingStreamUrl(token(), item.id, item.streamUrl);
        setPlaying(item);
        setPlayUrl(url);
        setPlayError('');
        const nextRecent = [
            Number(item.id),
            ...recentIds.filter(id => id !== Number(item.id))
        ].slice(0, RECORDINGS_RECENT_IDS_MAX);
        saveRecentRecordingIds(nextRecent);
        setRecentIds(nextRecent);
        logger.info(`播放录制 | recordingId=${item.id} meetingNo=${item.meetingNo}`);
        void loadSummaryStatus(item);
    };
    /**
     * 从会议历史跳转时，列表就绪后自动打开对应录像。
     * onPlay 每次渲染都会重建；用 ref 保证同一 recordingId 只自动打开一次。
     */
    useEffect(() => {
        if (!initialRecordingId || items.length === 0) {
            return;
        }
        if (openedInitialRecordingRef.current === initialRecordingId) {
            return;
        }
        const target = items.find(item => Number(item.id) === Number(initialRecordingId));
        if (!target) {
            logger.warn(`历史指定录像不在当前列表 | recordingId=${initialRecordingId}`);
            openedInitialRecordingRef.current = initialRecordingId;
            return;
        }
        openedInitialRecordingRef.current = initialRecordingId;
        logger.info(`从历史自动打开录像 | recordingId=${initialRecordingId}`);
        onPlay(target);
    }, [initialRecordingId, items, onPlay]);
    const onRegenerate = async () => {
        if (!summaryRecordingId) {
            return;
        }
        pollCancelledRef.current = false;
        setShowSummaryCta(false);
        setSummaryBusy(true);
        setSummaryGenerating(true);
        setSummaryError('');
        setSummary(null);
        setTranscript([]);
        try {
            let next = await regenerateRecordingSummaryById(serviceUrl, summaryRecordingId);
            if (next.status !== REC_SUMMARY_STATUS_READY
                && next.status !== REC_SUMMARY_STATUS_FAILED) {
                next = await pollUntilDone(summaryRecordingId);
            }
            await applyReady(summaryRecordingId, next);
        }
        catch (e) {
            if (e?.message === 'cancelled') {
                return;
            }
            setSummaryBusy(false);
            setSummaryGenerating(false);
            setSummaryError(e?.message || M.RECORDINGS_SUMMARY_LOAD_FAILED);
        }
    };
    const onSeek = (startMs) => {
        const video = videoRef.current;
        if (!video) {
            return;
        }
        video.currentTime = Math.max(0, startMs / MS_PER_SECOND);
        video.play().catch(() => undefined);
        logger.info(`会议总结跳转录像 | startMs=${startMs}`);
    };
    const onDelete = (item) => {
        if (!window.confirm(M.RECORDINGS_CONFIRM_DELETE)) {
            return;
        }
        setBusy(true);
        setError('');
        portalApi.deleteRecording(token(), item.id)
            .then(() => {
            if (playing?.id === item.id) {
                closePlayer();
            }
            else if (summaryRecordingId === Number(item.id)) {
                resetSummaryUi();
            }
            logger.info(`删除录制成功 | recordingId=${item.id}`);
            return load();
        })
            .catch((e) => {
            const msg = e instanceof PortalApiError ? e.message : M.ACTION_FAILED;
            setError(msg);
            logger.error(`删除录制失败 | recordingId=${item.id} error=${e?.message}`);
        })
            .finally(() => setBusy(false));
    };
    const showSummaryPanel = Boolean(!showSummaryCta && (summaryRecordingId || summaryError || summaryBusy || summary));
    const isDetailMode = Boolean(playing || showSummaryPanel || showSummaryCta);
    const activeRecordingId = playing?.id ?? summaryRecordingId;
    const ownerName = getStoredUser()?.displayName || M.NAV_RECORDINGS;
    const ownerInitial = ownerName.slice(0, 1).toUpperCase();
    const usedBytes = items.reduce((sum, item) => sum + (item.fileSizeBytes || 0), 0);
    let visibleItems = items;
    if (tab === 'recent') {
        const order = new Map(recentIds.map((id, index) => [id, index]));
        visibleItems = items
            .filter(item => order.has(Number(item.id)))
            .sort((a, b) => (order.get(Number(a.id)) ?? 0) - (order.get(Number(b.id)) ?? 0));
    }
    visibleItems = visibleItems.filter(item => matchesRecordingListFilter(item, listFilter));
    /**
     * 复制会议号。
     *
     * @param {IRecordingItem} item - 录制项。
     * @returns {Promise<void>}
     */
    const onShare = async (item) => {
        const meetingNo = item.meetingNo || item.roomName || '';
        if (!meetingNo) {
            setToast(M.RECORDINGS_SHARE_MISSING);
            logger.warn(`复制会议号失败：无会议号 | recordingId=${item.id}`);
            return;
        }
        try {
            await navigator.clipboard.writeText(meetingNo);
            setToast(M.RECORDINGS_SHARE_COPIED);
            setMenuRecordingId(null);
            logger.info(`已复制会议号 | recordingId=${item.id} meetingNo=${meetingNo}`);
        }
        catch (e) {
            setToast(M.ACTION_FAILED);
            logger.error(`复制会议号失败 | recordingId=${item.id} ${e?.message || e}`);
        }
    };
    /**
     * 渲染单条录制卡片。
     *
     * @param {IRecordingItem} item - 录制项。
     * @param {boolean} highlightActive - 是否高亮当前播放/总结项。
     * @param {boolean} grid - 是否宫格。
     * @param {boolean} compact - 详情侧栏精简。
     * @returns {React.ReactNode}
     */
    const renderListItem = (item, highlightActive, grid, compact) => {
        const isActive = highlightActive && activeRecordingId === item.id;
        const duration = formatRecordingDuration(item.durationSeconds);
        const timeText = formatRecordingListTime(item.createdAt);
        const meetingNo = formatMeetingNo(item.meetingNo || item.roomName);
        const metaParts = [timeText, meetingNo].filter(Boolean);
        const rowClass = [
            'portal-rec-row',
            isActive ? 'is-active' : '',
            grid ? 'is-grid' : ''
        ].filter(Boolean).join(' ');
        return (React.createElement("li", { className: rowClass, key: item.id, onClick: () => onPlay(item) },
            React.createElement("div", { className: `portal-rec-thumb${grid ? ' is-wide' : ''}` },
                item.fileAvailable === false ? null : (React.createElement("img", { alt: '', loading: 'lazy', onError: event => {
                        event.currentTarget.style.display = 'none';
                    }, src: portalApi.getRecordingThumbnailUrl(token(), item.id) })),
                duration ? React.createElement("span", { className: 'duration' }, duration) : null),
            React.createElement("div", { className: 'portal-rec-info' },
                React.createElement("div", { className: 'title' }, item.title || item.fileName),
                React.createElement("div", { className: 'meta' }, metaParts.join(' · '))),
            grid || compact ? null : (React.createElement("div", { className: 'portal-rec-owner' },
                React.createElement("span", { className: 'avatar' }, ownerInitial),
                React.createElement("span", null, ownerName))),
            grid ? null : (React.createElement("div", { className: 'portal-rec-actions', onClick: e => e.stopPropagation() },
                React.createElement("button", { "aria-label": M.RECORDINGS_SHARE, className: 'portal-rec-icon-btn', onClick: () => {
                        void onShare(item);
                    }, type: 'button' },
                    React.createElement("svg", { fill: 'none', height: '16', viewBox: '0 0 16 16', width: '16' },
                        React.createElement("path", { d: 'M6.2 9.8 9.8 6.2M7 5.2l.8-1.1a2.4 2.4 0 1 1 3.4 3.4L10 8.7M9 10.8l-.8 1.1a2.4 2.4 0 1 1-3.4-3.4L6 7.3', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.4' }))),
                React.createElement("button", { "aria-label": M.RECORDINGS_MORE, className: 'portal-rec-icon-btn', onClick: e => {
                        e.stopPropagation();
                        setFilterOpen(false);
                        setMenuRecordingId(menuRecordingId === item.id ? null : item.id);
                    }, type: 'button' }, "\u22EE"),
                menuRecordingId === item.id ? (React.createElement("div", { className: 'portal-rec-menu' },
                    React.createElement("button", { disabled: busy || item.fileAvailable === false, onClick: () => onPlay(item), type: 'button' }, M.RECORDINGS_PLAY),
                    React.createElement("button", { className: 'danger', disabled: busy, onClick: () => {
                            setMenuRecordingId(null);
                            onDelete(item);
                        }, type: 'button' }, M.RECORDINGS_DELETE))) : null))));
    };
    /**
     * 渲染录制列表区块。
     *
     * @param {object} options - 列表选项。
     * @returns {React.ReactNode}
     */
    const renderRecordingsList = ({ title, highlightActive, compact }) => {
        const grid = !compact && viewMode === 'grid';
        const emptyText = isRecordingListFilterActive(listFilter)
            ? M.RECORDINGS_FILTER_EMPTY
            : (tab === 'recent' ? M.RECORDINGS_EMPTY_RECENT : M.RECORDINGS_EMPTY);
        return (React.createElement("section", { className: 'portal-rec-list-wrap' },
            compact ? (React.createElement("div", { className: 'portal-section-head' },
                React.createElement("h3", null, title),
                React.createElement("button", { disabled: busy, onClick: () => load(), type: 'button' }, busy ? M.BUSY : M.RECORDINGS_REFRESH))) : (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'portal-rec-toolbar' },
                    React.createElement("div", { className: 'portal-rec-tabs' },
                        React.createElement("button", { className: tab === 'all' ? 'is-active' : undefined, onClick: () => setTab('all'), type: 'button' }, M.RECORDINGS_TAB_ALL),
                        React.createElement("button", { className: tab === 'recent' ? 'is-active' : undefined, onClick: () => setTab('recent'), type: 'button' }, M.RECORDINGS_TAB_RECENT),
                        React.createElement("button", { className: tab === 'mine' ? 'is-active' : undefined, onClick: () => setTab('mine'), type: 'button' }, M.RECORDINGS_TAB_MINE)),
                    React.createElement("button", { className: 'portal-rec-ghost', disabled: busy, onClick: () => load(), type: 'button' }, busy ? M.BUSY : M.RECORDINGS_REFRESH)),
                React.createElement("div", { className: 'portal-rec-storage' },
                    React.createElement("span", null, M.RECORDINGS_STORAGE_USED
                        .replace('{used}', formatSize(usedBytes))
                        .replace('{count}', String(items.length))),
                    React.createElement("div", { className: 'portal-rec-view-btns', onClick: e => e.stopPropagation() },
                        React.createElement("button", { "aria-label": M.RECORDINGS_FILTER_SCENE, className: `portal-rec-icon-btn${isRecordingListFilterActive(listFilter) || filterOpen ? ' is-active' : ''}`, ref: filterBtnRef, onClick: e => {
                                e.stopPropagation();
                                setMenuRecordingId(null);
                                if (filterOpen) {
                                    setFilterOpen(false);
                                    logger.info(`[录制] ${new Date().toISOString()} 关闭筛选面板`);
                                    return;
                                }
                                setDraftFilter({ ...listFilter });
                                setFilterOpen(true);
                                logger.info(`[录制] ${new Date().toISOString()} 打开筛选面板 | mobile=${isPortalMobile}`);
                            }, type: 'button' },
                            React.createElement("svg", { fill: 'none', height: '16', viewBox: '0 0 16 16', width: '16' },
                                React.createElement("path", { d: 'M2.5 3.5h11L9.5 8.2V12l-3 1.5V8.2L2.5 3.5Z', stroke: 'currentColor', strokeLinejoin: 'round', strokeWidth: '1.4' }))),
                        React.createElement("button", { "aria-label": M.RECORDINGS_VIEW_GRID, className: `portal-rec-icon-btn${viewMode === 'grid' ? ' is-active' : ''}`, onClick: () => setViewMode('grid'), type: 'button' },
                            React.createElement("svg", { fill: 'none', height: '16', viewBox: '0 0 16 16', width: '16' },
                                React.createElement("rect", { height: '5', rx: '1', stroke: 'currentColor', strokeWidth: '1.4', width: '5', x: '2.5', y: '2.5' }),
                                React.createElement("rect", { height: '5', rx: '1', stroke: 'currentColor', strokeWidth: '1.4', width: '5', x: '8.5', y: '2.5' }),
                                React.createElement("rect", { height: '5', rx: '1', stroke: 'currentColor', strokeWidth: '1.4', width: '5', x: '2.5', y: '8.5' }),
                                React.createElement("rect", { height: '5', rx: '1', stroke: 'currentColor', strokeWidth: '1.4', width: '5', x: '8.5', y: '8.5' }))),
                        React.createElement("button", { "aria-label": M.RECORDINGS_VIEW_LIST, className: `portal-rec-icon-btn${viewMode === 'list' ? ' is-active' : ''}`, onClick: () => setViewMode('list'), type: 'button' },
                            React.createElement("svg", { fill: 'none', height: '16', viewBox: '0 0 16 16', width: '16' },
                                React.createElement("path", { d: 'M3 4h10M3 8h10M3 12h10', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.6' }))),
                        filterOpen ? (React.createElement(RecordingFilterPanel, { anchorRef: filterBtnRef, draftFilter: draftFilter, isMobile: isPortalMobile, onClose: () => setFilterOpen(false), onConfirm: filter => {
                                setListFilter({ ...filter });
                                setFilterOpen(false);
                                logger.info(`[录制] ${new Date().toISOString()} 应用筛选 | scene=${filter.scene} time=${filter.time} start=${filter.startDate} end=${filter.endDate}`);
                            }, onDraftChange: setDraftFilter })) : null)))),
            toast ? React.createElement("div", { className: 'portal-rec-toast' }, toast) : null,
            visibleItems.length === 0 ? (React.createElement("div", { className: 'portal-empty' }, busy ? M.BUSY : emptyText)) : (React.createElement("ul", { className: `portal-rec-list${grid ? ' is-grid' : ''}` }, visibleItems.map(item => renderListItem(item, highlightActive, grid, Boolean(compact))))),
            !compact && visibleItems.length > 0 ? (React.createElement("div", { className: 'portal-rec-end' }, M.RECORDINGS_END)) : null));
    };
    const renderPlayer = () => {
        if (!playing || !playUrl) {
            return null;
        }
        return (React.createElement("section", { className: 'portal-recording-player' },
            React.createElement("div", { className: 'portal-section-head' },
                React.createElement("h3", null,
                    M.RECORDINGS_PLAYING,
                    "\uFF1A",
                    playing.title || playing.meetingNo || playing.fileName),
                React.createElement("button", { onClick: closePlayer, type: 'button' }, M.RECORDINGS_CLOSE_PLAYER)),
            React.createElement("video", { className: 'portal-recording-video', controls: true, key: playUrl, onError: onVideoError, ref: videoRef, src: playUrl })));
    };
    /**
     * 未总结时的「会议总结」入口按钮。
     *
     * @returns {React.ReactNode}
     */
    const renderSummaryCta = () => {
        if (!showSummaryCta || !playing) {
            return null;
        }
        return (React.createElement("section", { className: 'portal-recording-summary-cta' },
            React.createElement("button", { disabled: busy || summaryBusy || playing.fileAvailable === false, onClick: () => {
                    void onStartSummary(playing);
                }, type: 'button' }, M.RECORDINGS_VIEW_SUMMARY)));
    };
    const renderSummary = () => {
        if (!showSummaryPanel) {
            return null;
        }
        return (React.createElement("section", { className: 'portal-recording-summary' },
            React.createElement("div", { className: 'portal-section-head' },
                React.createElement("h3", null,
                    M.RECORDINGS_SUMMARY_TITLE,
                    summaryTitle ? `：${summaryTitle}` : ''),
                React.createElement("div", null,
                    React.createElement("button", { disabled: summaryBusy || !summaryRecordingId, onClick: onRegenerate, type: 'button' }, M.RECORDINGS_SUMMARY_REGENERATE))),
            summaryBusy ? (React.createElement("div", { className: 'portal-recording-summary-body' }, summaryGenerating
                ? M.RECORDINGS_SUMMARY_GENERATING
                : M.RECORDINGS_SUMMARY_LOADING)) : null,
            !summaryBusy && summaryError ? (React.createElement("div", { className: 'portal-error inline' }, summaryError)) : null,
            !summaryBusy && summary ? (React.createElement("div", { className: 'portal-recording-summary-body' },
                React.createElement("div", null, summary.overview || M.RECORDINGS_SUMMARY_EMPTY),
                summary.decisions?.length > 0 && (React.createElement("section", null,
                    React.createElement("strong", null, M.RECORDINGS_SUMMARY_DECISIONS),
                    React.createElement("ul", null, summary.decisions.map((d, i) => (React.createElement("li", { key: `d-${i}` }, d)))))),
                summary.actionItems?.length > 0 && (React.createElement("section", null,
                    React.createElement("strong", null, M.RECORDINGS_SUMMARY_ACTIONS),
                    React.createElement("ul", null, summary.actionItems.map((a, i) => (React.createElement("li", { key: `a-${i}` }, [a.owner, a.task, a.due].filter(Boolean).join(' · '))))))),
                summary.speakers?.length > 0 && (React.createElement("section", null,
                    React.createElement("strong", null, M.RECORDINGS_SUMMARY_SPEAKERS),
                    React.createElement("div", null, summary.speakers.join(', ')))),
                summary.riskPoints?.length > 0 && (React.createElement("section", null,
                    React.createElement("strong", null, M.RECORDINGS_SUMMARY_RISKS),
                    React.createElement("ul", null, summary.riskPoints.map((r, i) => (React.createElement("li", { key: `r-${i}` }, r)))))),
                transcript.length > 0 && (React.createElement("section", null,
                    React.createElement("strong", null, M.RECORDINGS_SUMMARY_TRANSCRIPT),
                    React.createElement("div", { className: 'meta' }, M.RECORDINGS_SUMMARY_SEEK_HINT),
                    React.createElement("ul", { className: 'portal-recording-transcript' }, transcript.map(chunk => (React.createElement("li", { key: chunk.chunkId, onClick: () => onSeek(chunk.startMs), onKeyDown: e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                onSeek(chunk.startMs);
                            }
                        }, role: 'button', tabIndex: 0 },
                        React.createElement("span", { className: 'meta' },
                            Math.floor(chunk.startMs / MS_PER_SECOND),
                            "s",
                            ' · ',
                            chunk.displayName),
                        ' ',
                        chunk.text)))))))) : null));
    };
    return (React.createElement("div", { className: `portal-recordings${isDetailMode ? ' is-detail' : ''}` },
        React.createElement("header", { className: 'portal-main-header' },
            React.createElement("div", null,
                React.createElement("h2", null, M.RECORDINGS_TITLE),
                React.createElement("p", null, M.NAV_RECORDINGS))),
        error || playError ? React.createElement("div", { className: 'portal-error inline' }, error || playError) : null,
        isDetailMode ? (React.createElement("div", { className: 'portal-recordings-detail' },
            React.createElement("div", { className: 'portal-recordings-detail-main' },
                renderPlayer(),
                renderSummaryCta(),
                renderSummary()),
            React.createElement("aside", { className: 'portal-recordings-detail-side' }, renderRecordingsList({
                title: M.RECORDINGS_SIDE_TITLE,
                highlightActive: true,
                compact: true
            })))) : (renderRecordingsList({
            title: M.RECORDINGS_TITLE,
            highlightActive: false
        }))));
}
